import { useState, useEffect, useCallback } from 'react';
import { ExtendedMarkerData } from '../../../../types/marker';
import { v4 as uuidv4 } from 'uuid';
import logger from '../../../../utils/logger';
import { markersService } from '../../../../services/supabase/markers.service';

interface UseMarkerFormProps {
  marker?: ExtendedMarkerData;
  mode: 'create' | 'edit';
  markerAddress?: string;
  isOpen: boolean;
}

export const useMarkerForm = ({ marker, mode: _mode, markerAddress, isOpen }: UseMarkerFormProps) => {
  // État initial du formulaire avec valeurs par défaut
  const [formData, setFormData] = useState<ExtendedMarkerData>(
    marker || {
      id: '',
      coordinates: [0, 0] as [number, number],
      address: markerAddress || '',
      categoryId: null,
      firstName: '',
      contact_address: '',
      contacts: [],
      actions: [{
        id: uuidv4(),
        markerId: '',
        date: new Date(),
        type: 'create',
        description: 'Création du marqueur',
        status: 'completed',
        is_system: true
      }],
      notes: '',
      createdAt: new Date(),
      updatedAt: new Date(),
      status: 'active',
      phone: '',
      email: '',
      imageUrl: undefined
    }
  );

  // Log détaillé lors de l'initialisation du formulaire
  useEffect(() => {
    if (_mode === 'create') {
      console.log(' [DEBUG] Initialisation formData pour création marqueur:', {
        mode: _mode,
        hasMarker: !!marker,
        actionsCount: formData.actions?.length,
        createAction: formData.actions?.find(a => a.type === 'create'),
        timestamp: new Date().toISOString()
      });
    }
  }, [_mode, marker, formData.actions]);

  // État pour stocker la catégorie d'origine avant modification
  const [originalCategoryId, setOriginalCategoryId] = useState(marker?.categoryId || null);
  
  // État pour forcer le rendu du select
  const [selectKey, setSelectKey] = useState<string>(marker?.categoryId || 'initial');

  // Mettre à jour formData quand le marker change (notamment après une mise à jour)
  useEffect(() => {
    if (marker && isOpen) {
      console.log('🔄 MISE À JOUR - formData depuis marker', {
        markerId: marker.id,
        markerCategoryId: marker.categoryId,
        hasImageUrl: !!marker.imageUrl,
        imageUrlLength: marker.imageUrl?.length || 0,
        imageUrlPrefix: marker.imageUrl?.substring(0, 50) || 'undefined',
        timestamp: new Date().toISOString()
      });
      
      // Préserver explicitement l'imageUrl lors de la mise à jour
      setFormData(prev => ({
        ...marker,
        imageUrl: marker.imageUrl || prev.imageUrl
      }));
    }
  }, [marker, isOpen]);

  // Mettre à jour originalCategoryId quand le marker change
  useEffect(() => {
    if (marker && isOpen) {
      setOriginalCategoryId(marker.categoryId);
      // Forcer le rendu du select avec une nouvelle clé
      setSelectKey(marker.categoryId || `marker-${Date.now()}`);
      console.log('🔄 MISE À JOUR - originalCategoryId et selectKey', {
        markerId: marker.id,
        oldCategoryId: originalCategoryId,
        newCategoryId: marker.categoryId,
        newSelectKey: marker.categoryId || `marker-${Date.now()}`,
        timestamp: new Date().toISOString()
      });
    }
  }, [marker, isOpen, originalCategoryId]);

  // Charger explicitement les actions du marqueur depuis la base de données
  useEffect(() => {
    if (marker) {
      logger.info('🔄 Mise à jour du marker dans la modale', {
        current: {
          id: formData.id,
          categoryId: formData.categoryId,
          actionsCount: formData.actions?.length,
          lastAction: formData.actions?.[formData.actions?.length - 1]
        },
        new: {
          id: marker.id,
          categoryId: marker.categoryId,
          actionsCount: marker.actions?.length,
          lastAction: marker.actions?.[marker.actions?.length - 1]
        }
      });

      const loadMarkerWithActions = async () => {
        try {
          // Récupérer le marqueur avec toutes ses actions depuis la base de données
          const updatedMarker = await markersService.getById(marker.id);
          
          if (updatedMarker) {
            logger.info('📋 Marqueur récupéré depuis la base de données:', {
              id: updatedMarker.id,
              actionsCount: updatedMarker.actions?.length,
              actions: updatedMarker.actions?.map(a => ({
                id: a.id,
                type: a.type,
                description: a.description,
                is_system: a.is_system
              }))
            });
            
            // Mettre à jour le formulaire avec les données récupérées
            const updatedFormData = {
              ...updatedMarker,
              // Extraire le prénom du premier contact s'il existe
              firstName: updatedMarker.contacts && updatedMarker.contacts.length > 0 
                ? updatedMarker.contacts[0].firstName || '' 
                : '',
              // Conserver l'imageUrl depuis la base de données
              imageUrl: updatedMarker.imageUrl,
              actions: updatedMarker.actions?.map(action => ({
                ...action,
                date: action.date instanceof Date ? action.date : new Date(action.date)
              })) || []
            };
            
            setFormData(updatedFormData);
          } else {
            // Fallback au comportement précédent si la récupération échoue
            const updatedFormData = {
              ...marker,
              firstName: marker.contacts && marker.contacts.length > 0 
                ? marker.contacts[0].firstName || marker.contacts[0].name || '' 
                : '',
              // Conserver l'imageUrl depuis le marker
              imageUrl: marker.imageUrl,
              actions: marker.actions?.map(action => ({
                ...action,
                date: action.date instanceof Date ? action.date : new Date(action.date)
              })) || []
            };
            
            setFormData(updatedFormData);
          }
        } catch (error) {
          logger.error('Erreur lors de la récupération du marqueur avec ses actions:', error);
          
          // Fallback au comportement précédent en cas d'erreur
          const updatedFormData = {
            ...marker,
            firstName: marker.contacts && marker.contacts.length > 0 
              ? marker.contacts[0].firstName || marker.contacts[0].name || '' 
              : '',
            // Conserver l'imageUrl depuis le marker en cas d'erreur
            imageUrl: marker.imageUrl,
            actions: marker.actions?.map(action => ({
              ...action,
              date: action.date instanceof Date ? action.date : new Date(action.date)
            })) || []
          };
          
          setFormData(updatedFormData);
        }
      };
      
      loadMarkerWithActions();
    }
  }, [marker]);

  // Log de l'état actuel du formData pour le débogage
  useEffect(() => {
    logger.info('📊 État actuel du formData', {
      id: formData.id,
      categoryId: formData.categoryId,
      actionsCount: formData.actions?.length,
      actions: formData.actions
    });
  }, [formData]);

  // Fonction pour mettre à jour un champ du formulaire
  const handleChange = useCallback((field: keyof ExtendedMarkerData, value: any) => {
    if (field === 'categoryId') {
      const newCategoryId = value || null;
      
      // Log détaillé pour le changement de catégorie
      console.log('🔄 CHANGEMENT CATÉGORIE - Début handleChange', {
        oldCategoryId: marker?.categoryId,
        newCategoryId: newCategoryId,
        formDataCategoryIdAvant: formData.categoryId,
        timestamp: new Date().toISOString()
      });
      
      logger.info('🔄 handleChange: Changement de catégorie détecté', {
        oldCategoryId: marker?.categoryId,
        newCategoryId: newCategoryId,
        actions: formData.actions?.length
      });
      
      // Mettre à jour la catégorie sans créer d'action immédiatement
      // Les actions de changement de catégorie seront créées lors de la soumission
      setFormData(prev => {
        logger.debug('Mise à jour du formData avec la nouvelle catégorie', {
          prevCategoryId: prev.categoryId,
          newCategoryId: newCategoryId,
          actionsCount: prev.actions?.length
        });
        
        return {
          ...prev,
          categoryId: newCategoryId
        };
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        updatedAt: new Date()
      }));
    }
  }, [formData, marker]);

  return {
    formData,
    setFormData,
    originalCategoryId,
    selectKey,
    handleChange
  };
};

